import { AlertTriangle, Clock, Calendar, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SelfCareGuidance from "./SelfCareGuidance";
import FollowUpPlanning from "./FollowUpPlanning";


interface TriageBasedRecommendationsProps {
  aiScore: number;
  category?: string;
  selectedSymptoms?: string[];
  additionalSymptoms?: string[];
  potentialDiagnoses?: string[];
  answers?: Record<string, any>;
  medicalEvidence?: any[];
}

interface TriageRecommendation {
  category: string;
  urgency: 'routine' | 'scheduled' | 'prompt' | 'urgent';
  timeline: string;
  description: string;
  actions: Array<{
    title: string;
    description: string;
    actionType: 'call' | 'link' | 'info';
    action?: string;
    priority: 'primary' | 'secondary';
  }>;
  icon: React.ReactNode;
  colorClass: string;
  bgClass: string;
  borderClass: string;
}

const TriageBasedRecommendations = ({ 
  aiScore, 
  category = "General", 
  selectedSymptoms = [], 
  additionalSymptoms = [],
  potentialDiagnoses = [],
  answers = {},
  medicalEvidence = []
}: TriageBasedRecommendationsProps) => {
  const getTriageRecommendation = (): TriageRecommendation => {
    if (aiScore >= 80) {
      return {
        category: "Urgent Attention",
        urgency: 'urgent',
        timeline: "Immediate action required",
        description: "Your symptoms indicate a potentially serious condition that requires immediate medical attention. Do not delay seeking emergency care.",
        actions: [
          {
            title: "Emergency Services",
            description: "Call 999 for immediate emergency medical response",
            actionType: 'call',
            action: 'tel:999',
            priority: 'primary'
          },
          {
            title: "Nearest A&E Department",
            description: "Go directly to your nearest Emergency Department",
            actionType: 'link',
            action: 'https://www.nhs.uk/nhs-services/urgent-and-emergency-care-services/',
            priority: 'primary'
          },
          {
            title: "Emergency Contacts",
            description: "Inform a family member or friend of your situation",
            actionType: 'info',
            priority: 'secondary'
          }
        ],
        icon: <Zap className="h-5 w-5" />,
        colorClass: "text-red-700",
        bgClass: "bg-red-50",
        borderClass: "border-red-200"
      };
    }

    if (aiScore >= 60) {
      return {
        category: "Prompt Consultation",
        urgency: 'prompt',
        timeline: "Within 24-48 hours",
        description: "Your symptoms are concerning and require prompt medical evaluation. Consider urgent care if your GP is not available.",
        actions: [
          {
            title: "NHS 111 Consultation",
            description: "Get immediate medical advice and guidance",
            actionType: 'call',
            action: 'tel:111',
            priority: 'primary'
          },
          {
            title: "GP Appointment",
            description: "Schedule an urgent appointment with your GP",
            actionType: 'link',
            action: 'https://www.nhs.uk/service-search/find-a-gp',
            priority: 'primary'
          },
          {
            title: "Urgent Care Center",
            description: "Visit if GP appointment not available within 48 hours",
            actionType: 'link',
            action: 'https://www.nhs.uk/nhs-services/urgent-and-emergency-care-services/',
            priority: 'secondary'
          }
        ],
        icon: <AlertTriangle className="h-5 w-5" />,
        colorClass: "text-orange-700",
        bgClass: "bg-orange-50",
        borderClass: "border-orange-200"
      };
    }

    if (aiScore >= 40) {
      return {
        category: "Medical Evaluation",
        urgency: 'scheduled',
        timeline: "Within 1-2 days",
        description: "Your symptoms warrant professional medical evaluation. Schedule an appointment with your healthcare provider for a thorough assessment.",
        actions: [
          {
            title: "GP Appointment",
            description: "Schedule appointment within 1-2 days for evaluation",
            actionType: 'link',
            action: 'https://www.nhs.uk/service-search/find-a-gp',
            priority: 'primary'
          },
          {
            title: "Symptom Monitoring",
            description: "Keep detailed record of symptoms and their progression",
            actionType: 'info',
            priority: 'primary'
          },
          {
            title: "NHS 111 Advice",
            description: "Call if symptoms worsen or new concerns arise",
            actionType: 'call',
            action: 'tel:111',
            priority: 'secondary'
          }
        ],
        icon: <Clock className="h-5 w-5" />,
        colorClass: "text-yellow-700",
        bgClass: "bg-yellow-50",
        borderClass: "border-yellow-200"
      };
    }

    // Score 0-39: Routine Care
    return {
      category: "Routine Care",
      urgency: 'routine',
      timeline: "Self-monitoring recommended",
      description: "Your symptoms appear mild and can typically be managed with self-care measures. Monitor symptoms and seek care if they worsen.",
      actions: [
        {
          title: "Self-Care Management",
          description: "Follow appropriate self-care measures for your symptoms",
          actionType: 'link',
          action: 'https://www.nhs.uk/live-well/',
          priority: 'primary'
        },
        {
          title: "Symptom Tracking",
          description: "Monitor symptoms and note any changes or worsening",
          actionType: 'info',
          priority: 'primary'
        },
        {
          title: "Escalation Guidelines",
          description: "Seek medical attention if symptoms persist beyond 3 days or worsen",
          actionType: 'info',
          priority: 'secondary'
        },
        {
          title: "GP Consultation",
          description: "Contact your GP if you have ongoing concerns",
          actionType: 'link',
          action: 'https://www.nhs.uk/service-search/find-a-gp',
          priority: 'secondary'
        }
      ],
      icon: <Calendar className="h-5 w-5" />,
      colorClass: "text-green-700",
      bgClass: "bg-green-50",
      borderClass: "border-green-200"
    };
  };

  const recommendation = getTriageRecommendation();

  const handleActionClick = (action: TriageRecommendation['actions'][0]) => {
    if (action.actionType === 'call' && action.action) {
      window.location.href = action.action;
    } else if (action.actionType === 'link' && action.action) {
      window.open(action.action, '_blank');
    }
  };

  const getUrgencyBadgeVariant = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'destructive';
      case 'prompt': return 'secondary';
      case 'scheduled': return 'outline';
      case 'routine': return 'default';
      default: return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Triage Recommendation */}
      <Card className={`p-4 ${recommendation.bgClass} ${recommendation.borderClass} border-2`}>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`${recommendation.colorClass}`}>
                {recommendation.icon}
              </div>
              <h3 className={`text-lg font-semibold ${recommendation.colorClass}`}>
                {recommendation.category}
              </h3>
            </div>
            <Badge variant={getUrgencyBadgeVariant(recommendation.urgency)} className="text-xs">
              {recommendation.timeline}
            </Badge>
          </div>

          {/* Description */}
          <p className={`text-sm ${recommendation.colorClass} leading-relaxed`}>
            {recommendation.description}
          </p>

          {/* Actions */}
          <div className="space-y-3">
            <h4 className={`text-sm font-medium ${recommendation.colorClass} mb-2`}>
              Immediate Actions:
            </h4>
            
            {/* Primary Actions */}
            <div className="space-y-2">
              {recommendation.actions
                .filter(action => action.priority === 'primary')
                .map((action, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`${recommendation.colorClass} bg-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 border`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${recommendation.colorClass}`}>
                            {action.title}
                          </p>
                          <p className={`text-xs ${recommendation.colorClass} opacity-80`}>
                            {action.description}
                          </p>
                        </div>
                        {(action.actionType === 'call' || action.actionType === 'link') && action.action && (
                          <Button
                            size="sm"
                            onClick={() => handleActionClick(action)}
                            className={`ml-2 text-xs px-3 py-1.5 ${
                              recommendation.urgency === 'urgent' 
                                ? 'bg-red-600 hover:bg-red-700' 
                                : recommendation.urgency === 'prompt'
                                ? 'bg-orange-600 hover:bg-orange-700'
                                : recommendation.urgency === 'scheduled'
                                ? 'bg-yellow-600 hover:bg-yellow-700'
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                          >
                            {action.actionType === 'call' ? 'Call' : 'Visit'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Secondary Actions */}
            {recommendation.actions.some(action => action.priority === 'secondary') && (
              <div className="mt-4 pt-3 border-t border-current border-opacity-20">
                <div className="space-y-2">
                  {recommendation.actions
                    .filter(action => action.priority === 'secondary')
                    .map((action, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className={`${recommendation.colorClass} opacity-60 text-xs mt-1`}>
                          •
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className={`text-xs font-medium ${recommendation.colorClass} opacity-80`}>
                                {action.title}
                              </p>
                              <p className={`text-xs ${recommendation.colorClass} opacity-60`}>
                                {action.description}
                              </p>
                            </div>
                            {(action.actionType === 'call' || action.actionType === 'link') && action.action && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleActionClick(action)}
                                className={`ml-2 text-xs px-2 py-1 border-current ${recommendation.colorClass} hover:bg-white hover:bg-opacity-20`}
                              >
                                {action.actionType === 'call' ? 'Call' : 'Visit'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Self-Care Guidance (for scores 0-40) */}
      {aiScore < 40 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Tailored Self-Care Guidance</h3>
          <SelfCareGuidance 
            aiScore={aiScore}
            category={category}
            selectedSymptoms={selectedSymptoms}
            additionalSymptoms={additionalSymptoms}
            answers={answers}
            medicalEvidence={medicalEvidence}
          />
        </div>
      )}

      {/* Follow-Up Planning (for all scores) */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-foreground">Follow-Up Plan</h3>
        <FollowUpPlanning 
          aiScore={aiScore}
          category={category}
          symptoms={[...selectedSymptoms, ...additionalSymptoms]}
          urgency={recommendation.urgency}
        />
      </div>

    </div>
  );
};

export default TriageBasedRecommendations;